import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Box, forwardRef, rem, Text, VStack } from '@bonusly/design-system';
import { Recipient } from '@bonusly/recipient-entities';

import { SelectorBox, SelectorProps } from '../selector/selector-box';
import { SelectorOption } from '../selector/selector-option';
import { useSelector } from '../selector/use-selector';
import { RecipientOption } from './recipient-option';

const LOADING_TIME_TO_SHOW_MESSAGE = 3000;

export const RecipientSelector = forwardRef<SelectorProps<Recipient>, 'div'>(({ command, hash, items, isLoading, query }, ref) => {
	const { t } = useTranslation('components');
	const [showLoadingMessage, setShowLoadingMessage] = useState(false);
	const { getOptionProps, getSelectorProps, selectedIndex } = useSelector({
		command,
		hash,
		items,
		ref,
	});

	useEffect(() => {
		if (isLoading) {
			const timeout = setTimeout(() => {
				setShowLoadingMessage(true);
			}, LOADING_TIME_TO_SHOW_MESSAGE);
			return () => {
				clearTimeout(timeout);
			};
		} else {
			setShowLoadingMessage(false);
		}
	}, [isLoading]);

	const loadingMessage = !showLoadingMessage ? undefined : (
		<Text color="text.softer" fontSize="sm" maxW={72} pt={2} textAlign="center">
			{t('rich_media.loading_is_taking_too_long')}
		</Text>
	);

	return (
		<SelectorBox data-testid="recipient-selector" isLoading={isLoading} loadingMessage={loadingMessage}>
			<VStack as="ul" alignItems="flex-start" maxH={rem(410)} overflowY="auto" role="listbox" spacing={0} {...getSelectorProps()}>
				{!query && (
					<Box p={2} color="text.softer" fontSize="sm" fontWeight="medium" lineHeight={5}>
						{t('rich_media.search_users.help_text')}
					</Box>
				)}

				{!items.length && (
					<Box p={2} color="text.softer" fontSize="sm" fontWeight="medium" lineHeight={5}>
						{t('rich_media.no_results')}
					</Box>
				)}

				{items.map((recipient, index) => {
					return (
						<SelectorOption isSelected={selectedIndex === index} key={recipient.id} {...getOptionProps(index)}>
							<RecipientOption recipient={recipient} />
						</SelectorOption>
					);
				})}
			</VStack>
		</SelectorBox>
	);
});
